import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { FormativeAssessmentResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface FormativeAssessmentCardProps {
  assessment: FormativeAssessmentResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
  },
  section: {
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  // Process section styles
  processHeader: {
    backgroundColor: "#dbeafe",
  },
  processTitle: {
    color: "#2563eb",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Assessment Tool section styles
  toolHeader: {
    backgroundColor: "#dcfce7",
  },
  toolTitle: {
    color: "#16a34a",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Teacher Feedback section styles
  feedbackHeader: {
    backgroundColor: "#f3e8ff",
  },
  feedbackTitle: {
    color: "#9333ea",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Student Reflection section styles
  reflectionHeader: {
    backgroundColor: "#fef3c7",
  },
  reflectionTitle: {
    color: "#d97706",
    fontSize: 11,
    fontWeight: "bold",
  },
});

const FormativeAssessmentDocument = ({
  assessment,
  customizationSettings,
}: FormativeAssessmentCardProps) => {
  const { t } = useTranslation();
  const markdownSectionStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
    listItem: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };
  return (
    <PdfLayout
      courseName={assessment.courseName || ""}
      gradeLevel={assessment.gradeLevel || 0}
      topic={assessment.title || ""}
      imageUrl={assessment.imageUrl}
      headerTextColor="#ca8a04"
      customizationSettings={customizationSettings}
      author={assessment.authorName || ""}
      createdDate={assessment.createdAt || ""}
    >
      <View style={styles.container}>
        {/* Process Section */}
        {assessment.process && (
          <View style={styles.section}>
            <View style={[styles.sectionHeader, styles.processHeader]}>
              <Text
                style={[
                  styles.processTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.processTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("formativeAssessment.process")}
              </Text>
            </View>
            <MarkdownRenderer
              content={assessment.process}
              customStyle={markdownSectionStyle}
            />
          </View>
        )}

        {/* Assessment Tool Section */}
        {assessment.assessmentTool && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.toolHeader]}>
              <Text
                style={[
                  styles.toolTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.toolTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("formativeAssessment.assessmentTool")}
              </Text>
            </View>
            <MarkdownRenderer
              content={assessment.assessmentTool}
              customStyle={markdownSectionStyle}
            />
          </View>
        )}

        {/* Teacher Feedback Section */}
        {assessment.teacherFeedback && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.feedbackHeader]}>
              <Text style={styles.feedbackTitle}>
                {t("formativeAssessment.teacherFeedback")}
              </Text>
            </View>
            <MarkdownRenderer
              content={assessment.teacherFeedback}
              customStyle={markdownSectionStyle}
            />
          </View>
        )}

        {/* Student Reflection Section */}
        {assessment.studentReflection && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.reflectionHeader]}>
              <Text
                style={[
                  styles.reflectionTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.reflectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("formativeAssessment.studentReflection")}
              </Text>
            </View>
            <MarkdownRenderer
              content={assessment.studentReflection}
              customStyle={markdownSectionStyle}
            />
          </View>
        )}
      </View>
    </PdfLayout>
  );
};

function FormativeAssessmentCardPDF(props: FormativeAssessmentCardProps) {
  return <FormativeAssessmentDocument {...props} />;
}

export default FormativeAssessmentCardPDF;
